import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, Calendar, Clock, Scale, Euro, Star, 
  ArrowRight, Sparkles, User, CheckCircle 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { StarRating } from './StarRating';
import { formatDate } from '../lib/utils';

const MatchScoreBadge = ({ score }) => {
  let color = 'bg-slate-100 text-slate-600';
  let label = 'Compatible';
  
  if (score >= 80) {
    color = 'bg-green-100 text-green-700';
    label = 'Excellent match';
  } else if (score >= 60) {
    color = 'bg-emerald-100 text-emerald-700';
    label = 'Très bon match';
  } else if (score >= 40) {
    color = 'bg-blue-100 text-blue-700';
    label = 'Bon match';
  }
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Sparkles className="w-3 h-3" />
      {score}% - {label}
    </span>
  );
};

export const MatchingTripCard = ({ trip, onContact }) => {
  return (
    <Card 
      className="border-slate-200 hover:shadow-lg hover:border-orange-200 transition-all"
      data-testid={`matching-trip-${trip.id}`}
    >
      <CardContent className="p-5">
        <div className="flex flex-col gap-4">
          {/* Match Score */}
          <div className="flex items-center justify-between">
            <MatchScoreBadge score={trip.match_score} />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-600 font-bold text-sm">
                  {trip.carrier_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="text-right">
                <p className="font-medium text-slate-900 text-sm">{trip.carrier_name}</p>
                {trip.carrier_rating ? (
                  <div className="flex items-center gap-1">
                    <StarRating rating={trip.carrier_rating} size="sm" />
                    <span className="text-xs text-slate-500">({trip.carrier_reviews})</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Nouveau</p>
                )}
              </div>
            </div>
          </div>

          {/* Route */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4 text-green-600" />
              </div>
              <span className="font-semibold text-slate-900">{trip.departure_city}</span>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4 text-red-600" />
              </div>
              <span className="font-semibold text-slate-900">{trip.arrival_city}</span>
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(trip.departure_date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {trip.departure_time}
            </span>
            <span className="flex items-center gap-1">
              <Scale className="w-4 h-4" />
              {trip.available_weight} kg max
            </span>
            <span className="flex items-center gap-1 font-semibold text-green-600">
              <Euro className="w-4 h-4" />
              {trip.price_per_kg}€/kg
            </span>
          </div>

          {/* Match Reasons */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Pourquoi ce match?</p>
            <div className="flex flex-wrap gap-2">
              {trip.match_reasons?.map((reason, idx) => (
                <span key={idx} className="text-xs text-slate-600 bg-white px-2 py-1 rounded">
                  {reason}
                </span>
              ))}
            </div>
          </div>

          {/* Action */}
          {onContact && (
            <div className="flex justify-end">
              <Button 
                onClick={() => onContact(trip)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`contact-trip-${trip.id}`}
              >
                Contacter le cotransporteur
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const MatchingTripsSection = ({ 
  trips, 
  loading, 
  title = "Cotransporteurs suggérés", 
  subtitle = "Ces cotransporteurs font un trajet similaire",
  onContact,
  emptyMessage = "Aucun cotransporteur ne fait ce trajet pour le moment"
}) => {
  if (loading) {
    return (
      <Card className="border-orange-200 bg-orange-50/50">
        <CardContent className="p-6 text-center">
          <div className="spinner mx-auto"></div>
          <p className="text-slate-500 mt-4">Recherche des trajets compatibles...</p>
        </CardContent>
      </Card>
    );
  }

  if (!trips || trips.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-8 text-center">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{emptyMessage}</p>
          <p className="text-sm text-slate-400 mt-2">
            Votre demande sera visible par tous les cotransporteurs
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 bg-gradient-to-br from-orange-50/50 to-white">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-orange-500" />
          {title}
        </CardTitle>
        <p className="text-sm text-slate-600">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {trips.map((trip) => (
          <MatchingTripCard key={trip.id} trip={trip} onContact={onContact} />
        ))}
      </CardContent>
    </Card>
  );
};

export default MatchingTripsSection;
